import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { MarketResearchHandoffConversation } from './MarketResearchHandoffConversation';
import { MarketResearchEditActivity } from './MarketResearchEditActivity';
import {
  getMarketResearchPostHandoffProgress,
  recordMarketResearchPostHandoffProgressView,
  type MarketResearchPostHandoffProgress as Progress,
} from '../api/marketResearchPostHandoffProgress';

const genericError = 'We could not load contracting progress. Please try again.';

const formatCompletedAt = (completedAt: string): string => new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
}).format(new Date(completedAt));

const formatHandoffAt = (handedOffAt: string): string => new Intl.DateTimeFormat(undefined, {
  year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
}).format(new Date(handedOffAt));

const makeViewId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // The value is an opaque, per-route idempotency key rather than a secret.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const value = Math.floor(Math.random() * 16);
    return (character === 'x' ? value : (value & 0x3) | 0x8).toString(16);
  });
};

export interface MarketResearchPostHandoffProgressProps {
  flowId: string;
  isSampleFlow?: boolean;
  /** Authenticated Customer identity for truthful chat-bubble alignment. */
  viewerEmail?: string | null;
  /** Returns to the Customer's valid Product Discovery or Brand-name J&A route. */
  onBack: () => void;
  /** Existing authorized stepper route; false leaves an Activity row descriptive. */
  onStageSelect?: (backendStageCode: number) => boolean;
}

/**
 * A Customer-only presentation route. It intentionally has no stage number,
 * no workflow controls, and no actions that can disclose or open contracting work.
 */
const MarketResearchPostHandoffProgress: React.FC<MarketResearchPostHandoffProgressProps> = ({
  flowId,
  isSampleFlow = false,
  viewerEmail,
  onBack,
  onStageSelect,
}) => {
  const [progress, setProgress] = useState<Progress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const telemetryRecordedRef = useRef(false);
  const viewIdRef = useRef<string | null>(null);

  const refresh = useCallback(async (isInitialLoad = false) => {
    setLoading(true);
    setError(false);
    try {
      const nextProgress = await getMarketResearchPostHandoffProgress(flowId);
      setProgress(nextProgress);
      // Record exactly one best-effort view only after the server has
      // authorized and returned the safe terminal projection. Refreshes,
      // retries, and rendering never create a second event for this route.
      if (isInitialLoad && !isSampleFlow && !telemetryRecordedRef.current) {
        telemetryRecordedRef.current = true;
        const viewId = viewIdRef.current ?? makeViewId();
        viewIdRef.current = viewId;
        void recordMarketResearchPostHandoffProgressView(flowId, viewId).catch(() => undefined);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [flowId, isSampleFlow]);

  useEffect(() => {
    void refresh(true);
  }, [refresh]);

  return (
    <Paper
      component="main"
      data-testid="contracting-progress-page"
      data-layout-width="available"
      sx={{ my: 4, p: { xs: 2.5, sm: 4 }, width: '100%', boxSizing: 'border-box' }}
    >
      <Stack spacing={3}>
        <Box>
          <Typography component="h1" variant="h4" gutterBottom>
            Handoff &amp; contracting progress
          </Typography>
          <Typography color="text.secondary">
            Your Market Research work is ready for contracting review. This list shows completed contracting workflow stages only.
          </Typography>
        </Box>

        <Box component="section" aria-labelledby="contracting-progress-title">
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1 }}>
            <Typography id="contracting-progress-title" component="h2" variant="h5">
              Contracting progress
            </Typography>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              onClick={() => void refresh()}
              disabled={loading}
              aria-label="Refresh contracting progress"
            >
              Refresh
            </Button>
          </Stack>

          {loading && !progress && (
            <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={20} />
              <Typography>Loading contracting progress</Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {genericError}
              {progress && <Typography component="div" variant="body2">Showing previously loaded contracting progress.</Typography>}
            </Alert>
          )}

          {progress && (
            <>
              <Paper component="section" variant="outlined" aria-labelledby="handoff-status-title" sx={{ mb: 2, p: 2 }}>
                <Typography component="h2" id="handoff-status-title" variant="h6">Handoff status</Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  {(progress.handoff.state === 'active' || progress.handoff.state === 'historical')
                    && progress.handoff.handed_off_at
                    ? <>Handed off <time dateTime={progress.handoff.handed_off_at}>{formatHandoffAt(progress.handoff.handed_off_at)}</time></>
                    : progress.handoff.state === 'date_unavailable'
                      ? 'Handoff date unavailable'
                      : 'Not yet handed off'}
                </Typography>
              </Paper>
              {progress.all_applicable_stages_completed && (
                <Alert severity="success" sx={{ mb: 2 }}>Contracting workflow stages are complete.</Alert>
              )}
              <Box component="ol" sx={{ display: 'grid', gap: 0.5, listStyle: 'none', m: 0, p: 0 }}>
                {progress.stages.map((stage, index) => {
                  const completed = stage.status === 'completed';
                  const statusLabel = completed ? 'Completed' : 'Not completed';
                  const completionText = stage.completed
                    ? `Completed by ${stage.completed.by_email} ${formatCompletedAt(stage.completed.at)}`
                    : stage.legacy_completion ? 'Complete' : null;
                  const checkboxLabel = completionText
                    ? `${stage.label}: ${statusLabel}; ${completionText}`
                    : `${stage.label}: ${statusLabel}`;
                  return (
                    <Box
                      component="li"
                      data-layout="number-checkbox-label"
                      data-testid={`contracting-progress-row-${stage.key}`}
                      key={stage.key}
                      sx={{
                        alignItems: 'center',
                        columnGap: 0.5,
                        display: 'grid',
                        gridTemplateColumns: '2.25rem auto minmax(0, 1fr)',
                        minHeight: 42,
                      }}
                    >
                      <Typography aria-hidden="true" component="span" sx={{ textAlign: 'right' }}>
                        {index + 1}.
                      </Typography>
                      <Box sx={{ display: 'flex' }}>
                        <Checkbox
                          checked={completed}
                          disabled
                          inputProps={{ 'aria-label': checkboxLabel }}
                          sx={{ p: 1 }}
                        />
                      </Box>
                      <Box>
                        <Typography>{stage.label}</Typography>
                        {completionText && (
                          <Typography color="text.secondary" variant="body2">
                            {completionText}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </Box>

        {progress && (
          <MarketResearchEditActivity
            flowId={flowId}
            isSampleFlow={isSampleFlow}
            handoffState={progress.handoff.state}
            onNavigate={onStageSelect}
          />
        )}

        {!isSampleFlow && (
          <MarketResearchHandoffConversation
            flowId={flowId}
            handoffState={progress?.handoff.state ?? 'not_handed_off'}
            viewerEmail={viewerEmail}
          />
        )}

        <Box data-testid="contracting-progress-footer" sx={{ display: 'flex', mt: 1 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack}>
            Back
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export default MarketResearchPostHandoffProgress;
